import { useEffect, useState, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { Empty, Skeleton, Segmented, Tooltip } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { wordListsDataSelector, wordsListsIsLoadedStatusSelector } from '../../../../store/wordLists'
import { fetchWorldLists } from '../../../../store/wordLists/extraReducers'
import { userSelector } from '../../../../store/global'
import { WordList } from '../../../../shared/types/api'
import LearnCardDialog from '../../../modalwindows/LearnCardDialog'
import { NavigationPages } from '../../../../common/navigation/navigation-pages'
import Constants from '../../../../const/Constants'
import { useNavigate } from 'react-router-dom'
import { BookOutlined, CheckOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import './garden.scss'

type GridSize = 'compact' | 'medium' | 'large'

type TreeStatus = 'WATERED' | 'NEED_WATER' | 'DEAD' | 'JUST_PLANTED'

const STATUS_BG: Record<TreeStatus, string> = {
    WATERED: '#e8f5e9',
    NEED_WATER: '#fff8e1',
    DEAD: '#fbe9e7',
    JUST_PLANTED: '#e3f2fd',
}

const STATUS_LABEL: Record<TreeStatus, string> = {
    WATERED: 'Здорово',
    NEED_WATER: 'Полить',
    DEAD: 'Засохло',
    JUST_PLANTED: 'Новое',
}

const REPEAT_INTERVALS = [1, 3, 7, 14, 30, 60, 120, 240, 365]

function getTreeStatus(wl: WordList): TreeStatus {
    return (wl.tree?.status as TreeStatus) || 'JUST_PLANTED'
}

function getDaysUntilRepeat(wl: WordList): number | null {
    if (!wl.repeatedDate) return null
    const repeated = new Date(wl.repeatedDate)
    const now = new Date()
    const daysSince = Math.floor((now.getTime() - repeated.getTime()) / 86400000)
    const interval = REPEAT_INTERVALS.find(i => i > daysSince) ?? 365
    return Math.max(0, interval - daysSince)
}

function getProgressPercent(wl: WordList): number {
    if (!wl.repeatedDate) return 0
    const repeated = new Date(wl.repeatedDate)
    const now = new Date()
    const daysSince = Math.floor((now.getTime() - repeated.getTime()) / 86400000)
    const interval = REPEAT_INTERVALS.find(i => i > daysSince) ?? 365
    return Math.min(100, Math.round((daysSince / interval) * 100))
}

function getProgressColor(percent: number): string {
    if (percent >= 100) return '#ff4d4f'
    if (percent >= 80) return '#ff9800'
    if (percent >= 50) return '#ffc107'
    return '#4CAF50'
}

function getTreeImageUrl(wl: WordList): string {
    const treeId = wl.tree?.id ?? 1
    const status = getTreeStatus(wl)
    return `${Constants.queryConst}/v1/trees/image/${treeId}/${status}`
}

const GardenPage = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const wordLists = useAppSelector(wordListsDataSelector)
    const isLoaded = useAppSelector(wordsListsIsLoadedStatusSelector)
    const user = useAppSelector(userSelector)
    const [gridSize, setGridSize] = useState<GridSize>('medium')

    useEffect(() => {
        if (!isLoaded) {
            dispatch(fetchWorldLists())
        }
    }, [dispatch, isLoaded])

    const userLists = useMemo(() => {
        if (!wordLists) return []
        return [...wordLists]
            .filter((wl: WordList) => wl.type === 'USER' || wl.type === 'COMMUNITY')
            .sort((a: WordList, b: WordList) => {
                const daysA = getDaysUntilRepeat(a) ?? 999
                const daysB = getDaysUntilRepeat(b) ?? 999
                return daysA - daysB
            })
    }, [wordLists])

    const needWaterCount = useMemo(() => {
        return userLists.filter((wl: WordList) => {
            const s = getTreeStatus(wl)
            return s === 'NEED_WATER' || s === 'DEAD'
        }).length
    }, [userLists])

    const [selectedList, setSelectedList] = useState<WordList | null>(null)

    const handleCardClick = (wl: WordList) => {
        setSelectedList(wl)
    }

    if (!isLoaded) {
        return (
            <div className="garden">
                <Helmet title="VoxLingua: Мой сад" />
                <div className="garden__header">
                    <Skeleton.Input active style={{ width: 200, height: 32 }} />
                </div>
                <div className="garden__grid garden__grid--medium">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="garden-card garden-card--skeleton">
                            <Skeleton.Avatar active shape="square" size={80} />
                            <Skeleton.Input active size="small" style={{ width: 100, marginTop: 8 }} />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (userLists.length === 0) {
        return (
            <div className="garden">
                <Helmet title="VoxLingua: Мой сад" />
                <div className="garden__header">
                    <h1 className="garden__title">Мой сад</h1>
                </div>
                <div className="garden__empty">
                    <div className="garden__empty-content">
                        <Typography.Title level={4} style={{ margin: 0 }}>
                            Твой сад пока пуст
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Добавь списки слов — и здесь вырастут деревья, за которыми нужно ухаживать
                        </Typography.Text>
                        <div className="garden__empty-actions">
                            <Button
                                type="primary"
                                icon={<BookOutlined />}
                                size="large"
                                onClick={() => navigate(NavigationPages.DICTIONARY)}
                            >
                                Создать в словаре
                            </Button>
                            <Button
                                icon={<CheckOutlined />}
                                size="large"
                                onClick={() => navigate(NavigationPages.DONE_WORDS_LISTS)}
                            >
                                Взять готовые списки
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="garden">
            <Helmet title="VoxLingua: Мой сад" />

            <div className="garden__header">
                <div className="garden__header-left">
                    <h1 className="garden__title">Мой сад</h1>
                    <span className="garden__summary">
                        {userLists.length} {userLists.length === 1 ? 'дерево' : 'деревьев'}
                        {needWaterCount > 0 && (
                            <span className="garden__summary-water">
                                {' '}&middot; {needWaterCount} нужен полив
                            </span>
                        )}
                    </span>
                </div>
                <div className="garden__header-right">
                    {user?.statistic?.countDaysTrack > 0 && (
                        <Tooltip title={`${user.statistic.countDaysTrack} дней подряд`}>
                            <span className="garden__streak">
                                {user.statistic.countDaysTrack}d
                            </span>
                        </Tooltip>
                    )}
                    <Segmented
                        size="small"
                        value={gridSize}
                        onChange={(v) => setGridSize(v as GridSize)}
                        options={[
                            { label: '5', value: 'compact' },
                            { label: '3', value: 'medium' },
                            { label: '2', value: 'large' },
                        ]}
                    />
                </div>
            </div>

            <div className={`garden__grid garden__grid--${gridSize}`}>
                {userLists.map((wl: WordList) => {
                    const status = getTreeStatus(wl)
                    const bgColor = STATUS_BG[status]
                    const progress = getProgressPercent(wl)
                    const progressColor = getProgressColor(progress)
                    const daysLeft = getDaysUntilRepeat(wl)
                    const showWater = status === 'NEED_WATER' || status === 'DEAD'

                    return (
                        <button
                            key={String(wl.id)}
                            className={`garden-card garden-card--${gridSize}`}
                            style={{ backgroundColor: bgColor }}
                            onClick={() => handleCardClick(wl)}
                            type="button"
                        >
                            <img
                                className="garden-card__tree"
                                src={getTreeImageUrl(wl)}
                                alt={wl.listName}
                                loading="lazy"
                            />

                            {gridSize !== 'compact' && (
                                <div className="garden-card__info">
                                    <span className="garden-card__name">{wl.listName}</span>
                                    <span className="garden-card__count">
                                        {wl.countWords ?? 0} слов
                                    </span>
                                </div>
                            )}

                            {gridSize === 'large' && (
                                <div className="garden-card__meta">
                                    {daysLeft !== null && (
                                        <span className="garden-card__days">
                                            {daysLeft === 0 ? 'Повторить сегодня' : `${daysLeft}д до повтора`}
                                        </span>
                                    )}
                                    {showWater && (
                                        <span className="garden-card__water-badge">
                                            {STATUS_LABEL[status]}
                                        </span>
                                    )}
                                </div>
                            )}

                            <div className="garden-card__progress">
                                <div
                                    className="garden-card__progress-bar"
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor: progressColor,
                                    }}
                                />
                            </div>
                        </button>
                    )
                })}
            </div>

            {selectedList && (
                <LearnCardDialog
                    wordList={selectedList}
                    open={!!selectedList}
                    close={() => setSelectedList(null)}
                />
            )}
        </div>
    )
}

export default GardenPage
